// (portfolio-20261027) Portfolio panel — every wallet and every open position in
// one read-only place, the way Axiom / Photon / GMGN / Hyperliquid lay it out:
// summary (equity · available · unrealized · realized by period), wallets,
// open positions grouped Spot / Paper / Futures / Bot, and recent activity.
// Read-only: rows open the chart, where the existing buy/sell controls live.
// Data: Helius proxy RPC (wallet balances/holdings), Dexscreener (prices, same
// batch + cache as the Wallets panel), the app socket (HL + bot, read-only).
// Other wallets' balances load when the panel opens / on refresh — no timer.
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { PublicKey } from '@solana/web3.js'
import { getSOLBalance, getAllTokenHoldings } from '../utils/jupiter'
import { fetchMarketBatch, holdingDetails } from './WalletHoldings'
import { useLocalWallet } from './WalletContext'
import { useDualWallet } from './DualWalletContext'
import { findEmpty, closeEmpty } from '../utils/reclaimRent'   // reclaim-20261028
import { paperAccount, paperHistory, PAPER_EVENT, PAPER_START_SOL } from '../utils/paperTrade'
import { PERIODS, periodStart, spotLedger, sumSince, paperEvents, hlEvents, paperOpen, botOpen, hideDust, sortRows, mergeActivity } from '../utils/portfolio'

const PREFS_KEY = 'cheddar_portfolio_prefs'
const PAPER_KEY = 'cheddar_paper_v1'
const loadPrefs = () => { try { return { period: 'today', unit: 'usd', dust: true, sort: 'value', ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') } } catch { return { period: 'today', unit: 'usd', dust: true, sort: 'value' } } }
const paperPositions = () => { try { return JSON.parse(localStorage.getItem(PAPER_KEY) || 'null')?.pos || {} } catch { return {} } }
const short = (a) => (a ? `${String(a).slice(0, 4)}…${String(a).slice(-4)}` : '')
const toPk = (k) => { try { return typeof k === 'string' ? new PublicKey(k) : k } catch { return null } }
const pkStr = (k) => { try { return k ? (typeof k === 'string' ? k : k.toBase58()) : '' } catch { return '' } }

// One read-only request on the shared app socket (same envelope useTrading uses).
function wsAsk(ws, action, type, timeout = 8000) {
  return new Promise((resolve, reject) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return reject(new Error('socket closed'))
    const h = (ev) => { try { const m = JSON.parse(ev.data); if (m.type === type || m.type === 'hl_error') { done(); resolve(m) } } catch {} }
    const t = setTimeout(() => { done(); reject(new Error('timeout')) }, timeout)
    const done = () => { ws.removeEventListener('message', h); clearTimeout(t) }
    ws.addEventListener('message', h)
    ws.send(JSON.stringify({ action }))
  })
}

export default function PortfolioPanel({ publicKey, hlWallet, tradeHistory, wsRef, solPriceRef, currentToken, rawTradesRef, lastBucketMcapRef, onSelectMint, onSelectFut }) {
  const { otherWallets = [], paperActive, paperAdded } = useLocalWallet() || {}
  const [prefs, setPrefs] = useState(loadPrefs)
  const setPref = (k, v) => setPrefs(p => { const n = { ...p, [k]: v }; try { localStorage.setItem(PREFS_KEY, JSON.stringify(n)) } catch {} return n })
  const [tab, setTab] = useState('positions')
  const [loading, setLoading] = useState(false)
  const [solBal, setSolBal] = useState({})          // pub -> SOL
  const [holdings, setHoldings] = useState([])      // active wallet tokens
  const [market, setMarket] = useState({})          // mint -> {priceUsd, mcapUsd, symbol}
  const [hl, setHl] = useState({ positions: [], account: null, fills: [] })
  const [botSlots, setBotSlots] = useState([])
  const [botDry, setBotDry] = useState(true)
  const [paperTick, setPaperTick] = useState(0)
  const [, setTick] = useState(0)
  const activeStr = pkStr(publicKey)
  // reclaim-20261028: empty token accounts on the active wallet -> rent back.
  const dual = useDualWallet()
  const canSign = !!publicKey && !!dual?.isReady && !paperActive
  const [reclaim, setReclaim] = useState(null)      // { accounts, lamports } from findEmpty
  const [rcStep, setRcStep] = useState('idle')      // idle | confirm | busy | done | error
  const [rcMsg, setRcMsg] = useState('')
  const solUsd = solPriceRef?.current || 0

  // Paper changes (buy/sell anywhere) re-read instantly.
  useEffect(() => {
    const on = () => setPaperTick(t => t + 1)
    window.addEventListener(PAPER_EVENT, on)
    return () => window.removeEventListener(PAPER_EVENT, on)
  }, [])
  // Light local re-render so the charted coin's paper mark + SOL price stay live (no network).
  useEffect(() => { const iv = setInterval(() => setTick(t => t + 1), 3000); return () => clearInterval(iv) }, [])

  // Bot: passive listener on bot_status (the bot already pushes it). Nothing is sent.
  useEffect(() => {
    const ws = wsRef?.current
    if (!ws) return
    const h = (ev) => {
      let m; try { m = JSON.parse(ev.data) } catch { return }
      if ((m.type || m.action) !== 'bot_status') return
      setBotSlots(Array.isArray(m.slots) && m.slots.length ? m.slots : (m.status ? [{ token: m.token, state: m.state, status: m.status }] : []))
      setBotDry(m.dryRun !== false)
    }
    ws.addEventListener('message', h)
    return () => ws.removeEventListener('message', h)
  }, [wsRef, wsRef?.current])

  const paperPos = useMemo(() => paperPositions(), [paperTick]) // eslint-disable-line react-hooks/exhaustive-deps
  const paperHist = useMemo(() => paperHistory(), [paperTick]) // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(async () => {
    setLoading(true)
    const pubs = [activeStr, ...otherWallets].filter((p, i, a) => p && a.indexOf(p) === i)
    const balP = Promise.all(pubs.map(async p => [p, await getSOLBalance(toPk(p)).catch(() => 0)]))
      .then(list => setSolBal(Object.fromEntries(list)))
    let mints = []
    const holdP = (async () => {
      if (!publicKey) { setHoldings([]); return }
      try {
        const raw = await getAllTokenHoldings(toPk(publicKey))
        const list = (raw || []).filter(t => t.uiAmount > 0)
        mints = list.map(t => t.mint)
        setHoldings(list)
      } catch { /* keep last */ }
    })()
    const hlP = (async () => {
      if (!hlWallet?.isReady) { setHl({ positions: [], account: null, fills: [] }); return }
      const ws = wsRef?.current
      try {
        const poll = await wsAsk(ws, 'hl_poll', 'hl_poll')
        const fills = await wsAsk(ws, 'hl_fills', 'hl_fills').catch(() => null)
        const pos = poll?.positions?.code === 0 && Array.isArray(poll.positions.data) ? poll.positions.data : []
        const accD = poll?.account?.code === 0 ? poll.account.data : null
        setHl({ positions: pos, account: Array.isArray(accD) ? accD[0] : accD,
          fills: fills?.code === 0 && Array.isArray(fills.data) ? fills.data : [] })
      } catch { /* socket down: leave futures as they were */ }
    })()
    const rcP = (async () => {
      if (!canSign) { setReclaim(null); return }
      try { setReclaim(await findEmpty(toPk(publicKey))) } catch { /* keep last */ }
    })()
    await Promise.all([balP, holdP, hlP, rcP])
    const extra = [...Object.keys(paperPositions()), ...botSlots.map(s => s.token).filter(Boolean),
      ...(tradeHistory?.history || []).slice(-40).map(t => t.mint), ...paperHistory().slice(-40).map(h => h.token)]
    const all = [...new Set([...mints, ...extra].filter(Boolean))]
    if (all.length) setMarket(await fetchMarketBatch(all))
    setLoading(false)
  }, [activeStr, otherWallets, publicKey, hlWallet?.isReady, wsRef, botSlots, tradeHistory?.history, canSign]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load when the panel opens and when the active wallet / HL unlock changes.
  const refreshRef = useRef(refresh); refreshRef.current = refresh
  useEffect(() => { refreshRef.current() }, [activeStr, otherWallets.join(','), hlWallet?.isReady, canSign]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ──
  const ledger = useMemo(() => spotLedger(tradeHistory?.history), [tradeHistory?.history])
  const since = periodStart(prefs.period)
  const sym = (m) => market[m]?.symbol || short(m)

  const spotRows = useMemo(() => holdings.map(t => {
    const mk = market[t.mint] || {}
    const d = holdingDetails({ ...t, priceUsd: mk.priceUsd, priceNative: mk.priceNative, mcapUsd: mk.mcapUsd }, solUsd)
    const lg = ledger.byMint[t.mint]
    return { kind: 'spot', id: t.mint, mint: t.mint, name: sym(t.mint), valueUsd: mk.priceUsd ? d.valueUsd : null,
      pnlUsd: d.pnlUsd, pnlPct: d.pnlPct, hasEntry: d.hasEntry, boughtSol: lg?.boughtSol || 0, soldSol: lg?.soldSol || 0 }
  }), [holdings, market, solUsd, ledger]) // eslint-disable-line react-hooks/exhaustive-deps

  const paperRows = Object.entries(paperPos).map(([tok, p]) => {
    let mark = 0, est = false
    if (tok === currentToken) mark = rawTradesRef?.current?.lastMcapOf?.('sell') || lastBucketMcapRef?.current || 0
    if (!(mark > 0) && market[tok]?.mcapUsd > 0 && solUsd > 0) { mark = market[tok].mcapUsd / solUsd; est = true }
    const o = paperOpen(p, mark) || {}
    return { kind: 'paper', id: tok, mint: tok, name: sym(tok), est, costSol: o.costSol,
      valueSol: o.valueSol, valueUsd: o.valueSol != null && solUsd ? o.valueSol * solUsd : null,
      pnlSol: o.pnlSol, pnlUsd: o.pnlSol != null && solUsd ? o.pnlSol * solUsd : null, pnlPct: o.pct }
  })

  const futRows = hl.positions.map(p => {
    const qty = Math.abs(parseFloat(p.qty) || 0), px = parseFloat(p.avgOpenPrice) || 0, u = parseFloat(p.unrealizedPNL) || 0
    const lev = parseFloat(p.leverage) || 1
    const ntl = qty * px
    return { kind: 'fut', id: p.symbol, sym: p.symbol, name: `${p.symbol} ${String(p.side || '').toUpperCase() === 'SHORT' ? 'S' : 'L'} ${lev}×`,
      valueUsd: ntl + u, pnlUsd: u, pnlPct: ntl > 0 ? (u / (ntl / lev)) * 100 : null, entry: px, liq: p.liqPrice }
  })

  const botRows = botSlots.map(botOpen).filter(Boolean).map(b => ({ kind: 'bot', id: b.token, mint: b.token, name: sym(b.token),
    pnlSol: b.pnlSol, pnlUsd: b.pnlSol != null && solUsd ? b.pnlSol * solUsd : null, pnlPct: b.pct,
    valueUsd: b.sizeSol && solUsd ? b.sizeSol * solUsd * (1 + (b.pct || 0) / 100) : null, sizeSol: b.sizeSol, state: b.state }))

  const shape = (rows) => sortRows(prefs.dust ? hideDust(rows, 1) : rows, prefs.sort)

  // Summary — real money only (paper and bot are shown on their own lines).
  const realSol = Object.values(solBal).reduce((a, b) => a + (b || 0), 0)
  const spotVal = spotRows.reduce((a, r) => a + (r.valueUsd || 0), 0)
  const hlEq = parseFloat(hl.account?.accountValue) || 0
  const equity = realSol * solUsd + spotVal + hlEq
  const availSol = solBal[activeStr] || 0
  const unreal = spotRows.reduce((a, r) => a + (r.pnlUsd || 0), 0) + futRows.reduce((a, r) => a + (r.pnlUsd || 0), 0)
  const hlEv = hlEvents(hl.fills)
  const realized = sumSince(ledger.events, since, 'pnlSol') * solUsd + sumSince(hlEv, since, 'pnlUsd')
  const pAcc = paperAccount()
  const pOpenSol = paperRows.reduce((a, r) => a + (r.valueSol ?? r.costSol ?? 0), 0)
  const pRealSol = sumSince(paperEvents(paperHist), since, 'pnlSol')

  // Money formatting in the chosen unit.
  const money = (usd, signed) => {
    if (usd == null || !Number.isFinite(usd)) return '—'
    const s = signed && usd > 0 ? '+' : usd < 0 ? '-' : ''
    if (prefs.unit === 'sol' && solUsd > 0) { const v = Math.abs(usd / solUsd); return `${s}${v >= 100 ? v.toFixed(1) : v.toFixed(3)} SOL` }
    const v = Math.abs(usd)
    return `${s}$${v >= 1e6 ? (v / 1e6).toFixed(2) + 'M' : v >= 1e4 ? (v / 1e3).toFixed(1) + 'K' : v.toFixed(2)}`
  }
  const solMoney = (sol, signed) => (sol == null ? '—' : prefs.unit === 'sol' || !solUsd ? `${signed && sol > 0 ? '+' : ''}${sol.toFixed(3)} SOL` : money(sol * solUsd, signed))
  const pct = (p) => (p == null || !Number.isFinite(p) ? '' : `${p >= 0 ? '+' : ''}${p.toFixed(1)}%`)
  const cls = (v) => (v > 0 ? 'pf-up' : v < 0 ? 'pf-down' : '')

  const open = (r) => {
    if (r.kind === 'fut') onSelectFut?.(r.sym)
    else if (r.mint) onSelectMint?.(r.mint)
  }

  // Activity — spot swaps, paper fills, HL fills, newest first.
  const activity = mergeActivity([
    (tradeHistory?.history || []).slice(-60).map(t => ({ t: t.timestamp, src: 'Spot', side: t.side, name: sym(t.mint), mint: t.mint, amt: solMoney(t.solAmount) })),
    paperHist.map(h => ({ t: h.t, src: 'Paper', side: h.side, name: sym(h.token), mint: h.token,
      amt: h.side === 'buy' ? solMoney(h.sol) : `${solMoney(h.proceeds)}${Number.isFinite(h.pnl) ? ` (${solMoney(h.pnl, true)})` : ''}` })),
    hl.fills.slice(0, 60).map(f => ({ t: +f.time, src: 'Futures', side: String(f.side || '').toLowerCase().startsWith('b') ? 'buy' : 'sell', name: f.symbol, sym: f.symbol, fut: true,
      amt: `${f.qty} @ ${f.price}${+f.closedPnl ? ` (${money((+f.closedPnl) - (+f.fee || 0), true)})` : ''}` })),
  ], 60)

  const Group = ({ title, sub, rows, empty, render }) => (
    <div className="pf-group">
      <div className="pf-group-head"><span>{title}</span><span className="pf-dim">{sub}</span></div>
      {rows.length ? rows.map(render) : <div className="pf-empty">{empty}</div>}
    </div>
  )
  const Row = (r, mid) => (
    <div key={r.kind + r.id} className="pf-row" onClick={() => open(r)} title="Open chart">
      <span className="pf-name">{r.name}{r.est && <span className="pf-tag" title="Price is an estimate (Dexscreener mcap ÷ SOL price) — open the chart for the live mark">est.</span>}</span>
      <span className="pf-mid pf-dim">{mid}</span>
      <span className="pf-val">{r.kind === 'paper' ? solMoney(r.valueSol) : money(r.valueUsd)}</span>
      <span className={`pf-pnl ${cls(r.pnlUsd ?? r.pnlSol)}`}>
        {r.kind === 'paper' || r.kind === 'bot' ? solMoney(r.pnlSol, true) : money(r.pnlUsd, true)} <small>{pct(r.pnlPct)}</small>
      </span>
    </div>
  )

  const runReclaim = async () => {
    if (!reclaim?.accounts?.length || !canSign) return
    setRcStep('busy'); setRcMsg('Starting…')
    try {
      const r = await closeEmpty(reclaim.accounts, toPk(publicKey), dual.signTransaction,
        ({ i, n, state }) => setRcMsg(`Batch ${i}/${n} · ${state}…`))
      const bad = r.results.find(x => x.state !== 'confirmed' && x.state !== 'skipped')
      const got = `Closed ${r.closed} · +${(r.lamports / 1e9).toFixed(4)} SOL`
      if (bad) { setRcStep('error'); setRcMsg(`${r.closed ? got + ' · ' : ''}stopped: ${bad.error || bad.state}`) }
      else { setRcStep('done'); setRcMsg(got) }
    } catch (e) { setRcStep('error'); setRcMsg(e?.message || String(e)) }
    refresh()
  }
  const rcN = reclaim?.accounts?.length || 0
  const rcSol = (reclaim?.lamports || 0) / 1e9

  const walletList = [activeStr, ...otherWallets].filter((p, i, a) => p && a.indexOf(p) === i)

  return (
    <div className="pf-panel">
      <style>{PF_CSS}</style>
      <div className="pf-bar">
        {PERIODS.map(([k, l]) => <button key={k} className={`pf-chip${prefs.period === k ? ' on' : ''}`} onClick={() => setPref('period', k)}>{l}</button>)}
        <span className="pf-sp" />
        <button className="pf-chip" onClick={() => setPref('unit', prefs.unit === 'usd' ? 'sol' : 'usd')} title="Show values in USD or SOL">{prefs.unit === 'usd' ? '$' : 'SOL'}</button>
        <button className={`pf-chip${loading ? ' spin' : ''}`} onClick={() => refresh()} title="Reload balances, holdings and prices">⟳</button>
      </div>

      <div className="pf-body">
        <div className="pf-summary">
          <div><label>Equity</label><b>{money(equity)}</b></div>
          <div><label>Available</label><b>{availSol.toFixed(3)} SOL</b></div>
          <div><label>Unrealized</label><b className={cls(unreal)}>{money(unreal, true)}</b></div>
          <div><label>Realized · {PERIODS.find(p => p[0] === prefs.period)?.[1]}</label><b className={cls(realized)}>{money(realized, true)}</b></div>
        </div>

        <div className="pf-tabs">
          {[['positions', 'Positions'], ['wallets', 'Wallets'], ['activity', 'Activity']].map(([k, l]) =>
            <button key={k} className={`pf-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{l}</button>)}
          {tab === 'positions' && <>
            <span className="pf-sp" />
            <button className="pf-chip" onClick={() => setPref('sort', prefs.sort === 'value' ? 'pnl' : 'value')} title="Sort rows">sort: {prefs.sort}</button>
            <button className={`pf-chip${prefs.dust ? ' on' : ''}`} onClick={() => setPref('dust', !prefs.dust)} title="Hide positions under $1">hide &lt;$1</button>
          </>}
        </div>

        {tab === 'positions' && <>
          {Group({ title: 'Spot', sub: activeStr ? short(activeStr) : 'no wallet', rows: shape(spotRows), empty: publicKey ? 'No tokens' : 'Connect a wallet',
            render: r => Row(r, `in ${r.boughtSol ? r.boughtSol.toFixed(2) : '—'} · out ${r.soldSol ? r.soldSol.toFixed(2) : '—'}${r.hasEntry ? '' : ' · no entry'}`) })}
          {(paperAdded || paperRows.length > 0) && (
            Group({ title: `Paper${paperActive ? ' · active' : ''}`, sub: `cash ${pAcc.cash.toFixed(3)} · realized ${solMoney(pRealSol, true)}`, rows: shape(paperRows), empty: 'No paper positions',
              render: r => Row(r, `cost ${r.costSol?.toFixed(3)} SOL`) })
          )}
          {(hlWallet?.hasStoredKeys || futRows.length > 0) && (
            Group({ title: 'Futures', sub: hlWallet?.isReady ? `equity ${money(hlEq)}` : 'unlock HL wallet', rows: shape(futRows), empty: hlWallet?.isReady ? 'No open positions' : 'Locked',
              render: r => Row(r, `entry ${r.entry}${r.liq ? ` · liq ${(+r.liq).toFixed(4)}` : ''}`) })
          )}
          {botSlots.length > 0 && (
            Group({ title: `Bot · ${botDry ? 'DRY' : 'LIVE'}`, sub: 'not in totals', rows: botRows, empty: 'Flat',
              render: r => Row(r, `${r.state || ''}${r.sizeSol ? ` · ${r.sizeSol.toFixed(2)} SOL` : ''}`) })
          )}
        </>}

        {tab === 'wallets' && (
          <div className="pf-group">
            {walletList.map(p => (
              <div key={p} className="pf-row pf-static">
                <span className="pf-name">{short(p)}{p === activeStr && <span className="pf-tag on">active</span>}</span>
                <span className="pf-mid pf-dim">{p === activeStr && spotVal > 0 ? `+ tokens ${money(spotVal)}` : ''}</span>
                <span className="pf-val">{solBal[p] != null ? `${solBal[p].toFixed(3)} SOL` : '…'}</span>
                <span className="pf-pnl pf-dim">{solBal[p] != null && solUsd ? money(solBal[p] * solUsd + (p === activeStr ? spotVal : 0)) : ''}</span>
              </div>
            ))}
            {canSign && (rcN > 0 || rcStep !== 'idle') && (
              <div className="pf-row pf-static pf-reclaim">
                <span className="pf-name">Reclaim rent</span>
                <span className="pf-mid pf-dim">{rcStep === 'busy' || rcStep === 'done' || rcStep === 'error' ? rcMsg
                  : rcStep === 'confirm' ? `Close ${rcN} empty account${rcN === 1 ? '' : 's'}, +${rcSol.toFixed(4)} SOL?`
                  : `${rcN} empty account${rcN === 1 ? '' : 's'} · ${rcSol.toFixed(4)} SOL reclaimable`}</span>
                <span className="pf-val pf-wide pf-rc-btns">
                  {rcStep === 'idle' && <button className="pf-chip on" onClick={() => setRcStep('confirm')}>Reclaim</button>}
                  {rcStep === 'confirm' && <>
                    <button className="pf-chip on" onClick={runReclaim}>Confirm</button>
                    <button className="pf-chip" onClick={() => setRcStep('idle')}>Cancel</button>
                  </>}
                  {(rcStep === 'done' || rcStep === 'error') && <button className="pf-chip" onClick={() => { setRcStep('idle'); setRcMsg('') }}>OK</button>}
                </span>
              </div>
            )}
            {hlWallet?.hasStoredKeys && (
              <div className="pf-row pf-static">
                <span className="pf-name">Hyperliquid{hlWallet.maskedAddress ? ` ${hlWallet.maskedAddress}` : ''}</span>
                <span className="pf-mid pf-dim">{hlWallet.isReady ? `avail ${money(parseFloat(hl.account?.available) || 0)}` : 'locked'}</span>
                <span className="pf-val">{hlWallet.isReady ? money(hlEq) : '—'}</span><span className="pf-pnl" />
              </div>
            )}
            {(paperAdded || paperActive) && (
              <div className="pf-row pf-static">
                <span className="pf-name">Paper<span className="pf-tag">sim</span>{paperActive && <span className="pf-tag on">active</span>}</span>
                <span className="pf-mid pf-dim">cash {pAcc.cash.toFixed(3)} · open {pOpenSol.toFixed(3)}</span>
                <span className="pf-val">{(pAcc.cash + pOpenSol).toFixed(3)} SOL</span>
                <span className={`pf-pnl ${cls(pAcc.cash + pOpenSol - PAPER_START_SOL)}`}>{solMoney(pAcc.cash + pOpenSol - PAPER_START_SOL, true)}</span>
              </div>
            )}
            {!walletList.length && <div className="pf-empty">No wallets</div>}
          </div>
        )}

        {tab === 'activity' && (
          <div className="pf-group">
            {activity.length ? activity.map((a, i) => (
              <div key={i} className="pf-row" onClick={() => (a.fut ? onSelectFut?.(a.sym) : a.mint && onSelectMint?.(a.mint))} title="Open chart">
                <span className="pf-name"><span className={a.side === 'buy' ? 'pf-up' : 'pf-down'}>{a.side === 'buy' ? 'BUY' : 'SELL'}</span> {a.name}</span>
                <span className="pf-mid pf-dim">{a.src} · {new Date(a.t).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                <span className="pf-val pf-wide">{a.amt}</span>
              </div>
            )) : <div className="pf-empty">No trades yet</div>}
          </div>
        )}
      </div>
    </div>
  )
}

const PF_CSS = `
.pf-panel{height:100%;min-height:0;display:flex;flex-direction:column;overflow:hidden;background:var(--surface);font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--text-hi)}
.pf-bar,.pf-tabs{flex:0 0 auto;display:flex;align-items:center;gap:4px;padding:0 var(--panel-pad);min-height:var(--rail-h);border-bottom:1px solid var(--seam)}
.pf-bar{background:var(--surface-rail)}
.pf-sp{flex:1}
.pf-chip,.pf-tab{background:none;border:1px solid transparent;color:var(--text-mid);font:inherit;font-size:10px;padding:2px 6px;cursor:pointer}
.pf-chip:hover,.pf-tab:hover{color:var(--text-hi)}
.pf-chip.on{color:var(--accent);border-color:var(--seam)}
.pf-tab.on{color:var(--text-hi);border-bottom:1px solid var(--accent)}
.pf-chip.spin{animation:pfspin 1s linear infinite}
@keyframes pfspin{to{transform:rotate(360deg)}}
.pf-body{flex:1 1 0;min-height:0;overflow-y:auto}
/* actscroll-20261028: dark scrollbar (same look as chart history dropdown) */
.pf-body{scrollbar-width:thin;scrollbar-color:#333 transparent}
.pf-body::-webkit-scrollbar{width:4px;height:4px;background:transparent}
.pf-body::-webkit-scrollbar-track{background:transparent}
.pf-body::-webkit-scrollbar-thumb{background:#333;border-radius:2px}
.pf-body::-webkit-scrollbar-button{display:none}
.pf-summary{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:1px;background:var(--seam);border-bottom:1px solid var(--seam)}
.pf-summary>div{background:var(--surface);padding:6px var(--panel-pad);display:flex;flex-direction:column;gap:2px}
.pf-summary label{color:var(--text-lo);font-size:9px;text-transform:uppercase;letter-spacing:.04em}
.pf-summary b{font-weight:600;font-size:12px}
.pf-group-head{display:flex;justify-content:space-between;padding:6px var(--panel-pad) 3px;color:var(--text-mid);font-size:10px;text-transform:uppercase;letter-spacing:.04em}
.pf-row{display:grid;grid-template-columns:minmax(70px,1.2fr) minmax(0,1.6fr) auto minmax(90px,auto);gap:8px;align-items:center;padding:4px var(--panel-pad);cursor:pointer;border-bottom:1px solid var(--seam)}
.pf-row:hover{background:var(--surface-hover)}
.pf-row.pf-static{cursor:default}
.pf-name{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pf-mid{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:10px}
.pf-val{text-align:right;white-space:nowrap}
.pf-wide{grid-column:3 / 5}
.pf-pnl{text-align:right;white-space:nowrap}
.pf-pnl small{opacity:.75}
.pf-dim{color:var(--text-lo)}
.pf-up{color:var(--up)}.pf-down{color:var(--down)}
.pf-tag{margin-left:5px;font-size:9px;padding:0 3px;border:1px solid var(--seam);color:var(--text-mid)}
.pf-tag.on{color:var(--accent)}
.pf-empty{padding:6px var(--panel-pad);color:var(--text-lo)}
.pf-rc-btns{display:flex;justify-content:flex-end;gap:4px}
.pf-reclaim .pf-mid{white-space:normal}
`
